import { put, call, all, takeLatest, takeEvery } from "redux-saga/effects";
import { axiosGet, axiosPost, axiosPatch, axiosDelete } from "../util/axios";
import * as routers from "../util/routers";
import * as actionType from "../constants/action_types";
import * as actions from "../actions/index";
import { HTTP_SUCCESS_STATUSES } from "../constants/http_constants";
import { toast } from "react-toastify";

// House convention: every _REQ turns the global loader ON; every _RESPONSE turns
// it OFF. Every saga MUST dispatch a _RESPONSE action on both success AND error
// paths, otherwise the loader stays on forever.

function* fetchApps() {
  try {
    const { data: axiosData, status } = yield call(axiosGet, routers.oidcApps);
    if (!HTTP_SUCCESS_STATUSES.includes(status)) throw new Error(axiosData?.message || 'Failed to load apps');
    yield put(actions.FETCH_APPS_RESPONSE(axiosData?.data || []));
  } catch (err) {
    yield put(actions.FETCH_APPS_RESPONSE([]));
    toast.error(err.message || "Failed to load apps");
  }
}

function* createApp(action) {
  try {
    const { data, status } = yield call(axiosPost, routers.oidcApps, action.payload.dto);
    if (!HTTP_SUCCESS_STATUSES.includes(status)) throw new Error(data?.message || 'Create failed');
    yield put(actions.CREATE_APP_RESPONSE(data));
    // Refresh the list in the background so the tile is ready when the user
    // closes the credentials modal.
    yield call(fetchApps);
    if (action.payload.onSuccess) action.payload.onSuccess(data);
  } catch (err) {
    yield put(actions.CREATE_APP_RESPONSE(null));
    toast.error(err.message || "Failed to create app");
  }
}

function* updateApp(action) {
  try {
    const { data, status } = yield call(
      axiosPatch,
      `${routers.oidcApps}/${action.payload.id}`,
      action.payload.dto,
    );
    if (!HTTP_SUCCESS_STATUSES.includes(status)) throw new Error(data?.message || 'Update failed');
    yield call(fetchApps);
    // Upgrading a discovery app to SSO returns a one-time client_secret — route it
    // to the credentials screen via the same channel as create.
    if (data && data.client_secret) {
      yield put(actions.CREATE_APP_RESPONSE(data));
    }
    yield put(actions.UPDATE_APP_RESPONSE());
    if (action.payload.onSuccess) action.payload.onSuccess(data);
    toast.success("App updated");
  } catch (err) {
    yield put(actions.UPDATE_APP_RESPONSE());
    toast.error(err.message || "Failed to update app");
  }
}

function* rotateAppSecret(action) {
  try {
    const { data, status } = yield call(axiosPost, `${routers.oidcApps}/${action.payload.id}/rotate-secret`);
    if (!HTTP_SUCCESS_STATUSES.includes(status)) throw new Error(data?.message || 'Rotate failed');
    // Merge the app's client_id (from the tile) with the new secret from the API
    yield put(actions.ROTATE_APP_SECRET_RESPONSE({
      client_id: action.payload.client_id,
      client_secret: data.client_secret,
      appName: action.payload.appName,
    }));
  } catch (err) {
    yield put(actions.ROTATE_APP_SECRET_RESPONSE(null));
    toast.error(err.message || "Failed to rotate secret");
  }
}

function* disableApp(action) {
  try {
    const { status } = yield call(axiosPost, `${routers.oidcApps}/${action.payload.id}/disable`);
    if (!(status >= 200 && status < 300)) throw new Error('Disable failed');
    yield call(fetchApps);
    yield put(actions.DISABLE_APP_RESPONSE());
    toast.success("App disabled");
  } catch (err) {
    yield put(actions.DISABLE_APP_RESPONSE());
    toast.error(err.message || "Failed to disable app");
  }
}

function* enableApp(action) {
  try {
    const { status } = yield call(axiosPost, `${routers.oidcApps}/${action.payload.id}/enable`);
    if (!(status >= 200 && status < 300)) throw new Error('Enable failed');
    yield call(fetchApps);
    yield put(actions.ENABLE_APP_RESPONSE());
    toast.success("App enabled");
  } catch (err) {
    yield put(actions.ENABLE_APP_RESPONSE());
    toast.error(err.message || "Failed to enable app");
  }
}

function* deleteApp(action) {
  try {
    const { status } = yield call(axiosDelete, `${routers.oidcApps}/${action.payload.id}`);
    if (!(status >= 200 && status < 300)) throw new Error('Delete failed');
    yield call(fetchApps);
    yield put(actions.DELETE_APP_RESPONSE());
    toast.success("App deleted");
  } catch (err) {
    yield put(actions.DELETE_APP_RESPONSE());
    toast.error(err.message || "Failed to delete app");
  }
}

function* appsWatcherSaga() {
  yield all([
    takeLatest(actionType.FETCH_APPS_REQ, fetchApps),
    takeEvery(actionType.CREATE_APP_REQ, createApp),
    takeEvery(actionType.UPDATE_APP_REQ, updateApp),
    takeEvery(actionType.ROTATE_APP_SECRET_REQ, rotateAppSecret),
    takeEvery(actionType.DISABLE_APP_REQ, disableApp),
    takeEvery(actionType.ENABLE_APP_REQ, enableApp),
    takeEvery(actionType.DELETE_APP_REQ, deleteApp),
  ]);
}

export default appsWatcherSaga;
